import axios from "axios";
import { useState, useCallback } from "react";
import { toast } from "react-toastify";

const useReserveUnreserveSlot = () => {
  const [isLoading, setIsLoading] = useState(false);

  const reserveSlot = useCallback(async (doctorId, eventStartTime, reason) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const params = {
        event_start_time: eventStartTime,
      };
      if (reason) {
        params.reason = reason;
      }
      await axios.post(
        `/api/v1/phr-app/doctors/${doctorId}/reserve-slot`,
        {},
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            "ngrok-skip-browser-warning": "hgs",
          },
        }
      );
      toast.success("Slot reserved successfully");
      return true;
    } catch (error) {
      console.error(error, "error");
      const errorMessage = error.response?.data?.message;
      if (!error.response) {
        toast.error("Oops, something went wrong");
      } else {
        const messageContent = Array.isArray(errorMessage) ? (
          <ul className="ms-2 list-disc">
            {errorMessage.map((msg, index) => (
              <li key={index}>{msg}</li>
            ))}
          </ul>
        ) : (
          errorMessage || "An unknown error occurred"
        );
        toast.error(messageContent);
        if (error.response?.data?.statusCode === 401) {
          localStorage.removeItem("authToken");
          setTimeout(() => {
            window.location.href = "/";
          }, 1000);
        }
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unreserveSlot = useCallback(async (doctorId, eventStartTime) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(
        `/api/v1/phr-app/doctors/${doctorId}/reserve-slot`,
        {
          params: {
            event_start_time: eventStartTime,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "ngrok-skip-browser-warning": "hgs",
          },
        }
      );
      toast.success("Slot unreserved successfully");
      return true;
    } catch (error) {
      console.error(error, "error");
      const errorMessage = error.response?.data?.message;
      if (!error.response) {
        toast.error("Oops, something went wrong");
      } else {
        const messageContent = Array.isArray(errorMessage) ? (
          <ul className="ms-2 list-disc">
            {errorMessage.map((msg, index) => (
              <li key={index}>{msg}</li>
            ))}
          </ul>
        ) : (
          errorMessage || "An unknown error occurred"
        );
        toast.error(messageContent);
        if (error.response?.data?.statusCode === 401) {
          localStorage.removeItem("authToken");
          setTimeout(() => {
            window.location.href = "/";
          }, 1000);
        }
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const bulkReserveSlots = useCallback(async (doctorId, eventStartTimes, reason) => {
    setIsLoading(true);
    try {
      // Validate input
      if (!Array.isArray(eventStartTimes) || eventStartTimes.length === 0) {
        toast.error("Invalid slot times provided");
        return false;
      }

      const token = localStorage.getItem("authToken");
      
      // API expects an object with event_start_times array and optional reason
      const requestBody = {
        event_start_times: eventStartTimes,
      };
      if (reason) {
        requestBody.reason = reason;
      }

      console.log("Bulk reserve API call:", {
        url: `/api/v1/phr-app/doctors/${doctorId}/bulk-reserve`,
        body: requestBody,
        bodyType: typeof requestBody,
        bodyLength: eventStartTimes.length
      });

      await axios.post(
        `/api/v1/phr-app/doctors/${doctorId}/bulk-reserve`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "ngrok-skip-browser-warning": "hgs",
            "Content-Type": "application/json",
          },
        }
      );
      toast.success(`${eventStartTimes.length} slot(s) reserved successfully`);
      return true;
    } catch (error) {
      console.error("Bulk reserve error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error config:", error.config);
      
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || error.message;
      if (!error.response) {
        toast.error("Oops, something went wrong");
      } else {
        const messageContent = Array.isArray(errorMessage) ? (
          <ul className="ms-2 list-disc">
            {errorMessage.map((msg, index) => (
              <li key={index}>{msg}</li>
            ))}
          </ul>
        ) : (
          errorMessage || "An unknown error occurred"
        );
        toast.error(messageContent);
        if (error.response?.data?.statusCode === 401) {
          localStorage.removeItem("authToken");
          setTimeout(() => {
            window.location.href = "/";
          }, 1000);
        }
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    reserveSlot,
    unreserveSlot,
    bulkReserveSlots,
    isLoading,
  };
};

export default useReserveUnreserveSlot;
